import type { FundDetail } from "@/data/fund-detail-types";
import { useId } from "react";

interface DocCard {
  title: string;
  date: string;
  color: "orange" | "teal" | "grey" | "blue";
  href?: string;
  disabled: boolean;
}

const PATTERN_OFFSETS: { px: number; py: number; hx: number; hy: number }[] = [
  { px: 0, py: 0, hx: 2, hy: 2 },
  { px: 5, py: 3, hx: 8, hy: 1 },
  { px: 3, py: 7, hx: 1, hy: 5 },
  { px: 7, py: 2, hx: 4, hy: 8 },
];

const COLOR_MAP = {
  orange: "#ff5500",
  teal: "#00b29e",
  grey: "var(--etf-white3)",
  blue: "#4a80d4",
} as const;

function DocCardPattern({ index = 0, color }: { index?: number; color: keyof typeof COLOR_MAP }) {
  const id = useId();
  const i = index % PATTERN_OFFSETS.length;
  const px = PATTERN_OFFSETS[i]?.px ?? 0;
  const py = PATTERN_OFFSETS[i]?.py ?? 0;
  const hx = PATTERN_OFFSETS[i]?.hx ?? 2;
  const hy = PATTERN_OFFSETS[i]?.hy ?? 2;
  return (
    <div
      className="pointer-events-none absolute top-0 left-1/2 -mt-2 -ml-20 w-full h-full"
      style={{
        maskImage: "linear-gradient(to bottom, white, transparent)",
        WebkitMaskImage: "linear-gradient(to bottom, white, transparent)",
      }}
    >
      <svg
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        className="absolute inset-0 w-full h-full mix-blend-overlay opacity-[0.18]"
        style={{
          stroke: COLOR_MAP[color],
          maskImage: "radial-gradient(farthest-side at top, white, transparent)",
          WebkitMaskImage: "radial-gradient(farthest-side at top, white, transparent)",
        }}
      >
        <defs>
          <pattern id={id} x={px} y={py} width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M.5 20V.5H20" fill="none" />
          </pattern>
        </defs>
        <rect width="200" height="200" fill={`url(#${id})`} strokeWidth="0" />
        <rect x={hx * 20} y={hy * 20} width="20" height="20" fill="currentColor" opacity="0.06" />
      </svg>
    </div>
  );
}

function DocIcon({ color }: { color: string }) {
  return (
    <svg
      className="w-5 h-5 opacity-85"
      style={{ color }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      className="w-4 h-4 text-[var(--etf-white3)] opacity-0 -translate-x-[3px] translate-y-[3px] transition-all duration-200 group-hover:opacity-60 group-hover:translate-x-0 group-hover:translate-y-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="7" y1="17" x2="17" y2="7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  );
}

const COLOR_CYCLE: DocCard["color"][] = ["orange", "teal", "grey", "blue"];

export function BufferDocuments({ fund }: { fund: FundDetail }) {
  // URL presence is the toggle: a document without a URL is rendered disabled.
  const docs: DocCard[] = fund.documents.map((d, i) => ({
    title: d.name,
    date: d.date,
    color: COLOR_CYCLE[i % COLOR_CYCLE.length]!,
    href: d.href,
    disabled: !d.href,
  }));

  return (
    <section id="sec-documents" data-fund-section="documents" className="flex-1 flex flex-col">
      <div className="flex items-center justify-between px-10 max-md:px-4 py-6 border-b border-[var(--etf-border)]">
        <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
          <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
          Documents
        </h2>
      </div>

      <div className="grid grid-cols-3 max-md:grid-cols-2 max-[480px]:grid-cols-1 flex-1">
        {docs.map((doc, i) => {
          if (doc.disabled) {
            return (
              <div
                key={doc.title}
                aria-disabled="true"
                className="relative overflow-hidden px-6 max-md:px-5 max-[480px]:px-4 pt-6 max-[480px]:pt-[18px] pb-7 flex flex-col opacity-40 cursor-not-allowed pointer-events-none"
              >
                <DocCardPattern index={i} color={doc.color} />
                <div className="flex items-center justify-between">
                  <DocIcon color={COLOR_MAP[doc.color]} />
                </div>
                <div className="font-[family-name:var(--font-geist)] text-[13px] font-medium text-[var(--etf-white)] mt-auto max-md:mt-8 max-[480px]:mt-6 tracking-[0.01em] relative z-[2]">
                  {doc.title}
                </div>
              </div>
            );
          }
          return (
            <a
              key={doc.title}
              href={doc.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden px-6 max-md:px-5 max-[480px]:px-4 pt-6 max-[480px]:pt-[18px] pb-7 no-underline text-inherit cursor-pointer transition-[background] duration-200 flex flex-col hover:bg-white/[0.035]"
            >
              <DocCardPattern index={i} color={doc.color} />
              <div className="flex items-center justify-between">
                <DocIcon color={COLOR_MAP[doc.color]} />
                <ArrowIcon />
              </div>
              <div className="font-[family-name:var(--font-geist)] text-[13px] font-medium text-[var(--etf-white)] mt-auto max-md:mt-8 max-[480px]:mt-6 tracking-[0.01em] relative z-[2]">
                {doc.title}
              </div>
              <div className="font-[family-name:var(--font-geist)] text-[10px] text-[var(--etf-white3)] mt-[5px] opacity-65 tracking-[0.04em] relative z-[2]">
                {doc.date}
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
